import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Edit3, ExternalLink, FileText, History, MessageSquare, Paperclip, Plus, Search, Send, Trash2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import SlidingPanel from '../SlidingPanel/SlidingPanel';
import useClientTicketsRealtime from '../../hooks/useClientTicketsRealtime';
import styles from './ClientTicketQueue.module.css';

const unwrapList = (data) => Array.isArray(data) ? data : (data?.results || []);

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMoney = (value) => {
  const number = Number(value || 0);
  if (!number) return '0 ₽';
  return `${number.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`;
};

const getInitials = (value) => {
  const parts = String(value || 'CRM')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return (parts.slice(0, 2).map(part => part[0]).join('') || 'CRM').toUpperCase();
};

const getTicketPreview = (ticket) => {
  const content = ticket?.last_message?.content?.trim();
  if (content) return content;
  if (ticket?.last_message?.attachments?.length) return 'Файл без текста';
  return 'Без сообщений';
};

const statuses = [
  { value: 'waiting_crm', label: 'Требуют ответа' },
  { value: '', label: 'Все' },
  { value: 'waiting_client', label: 'Ответ отправлен' },
];

const documentTypes = [
  { value: 'client_request', label: 'Файл заявки с оборудованием', category: 'request' },
  { value: 'acceptance_act', label: 'Акт приема-передачи', category: 'request' },
  { value: 'pickup_ttn', label: 'ТТН забора', category: 'transport' },
  { value: 'shipping_ttn', label: 'ТТН отгрузки', category: 'transport' },
  { value: 'estimate', label: 'Смета', category: 'estimate' },
  { value: 'closing', label: 'Закрывающие документы', category: 'closing' },
  { value: 'upd', label: 'УПД', category: 'closing' },
  { value: 'act', label: 'Акт выполненных работ', category: 'closing' },
  { value: 'other', label: 'Прочее', category: 'other' },
];

const documentSections = [
  { value: 'request', label: 'Заявка' },
  { value: 'transport', label: 'Транспортные' },
  { value: 'estimate', label: 'Сметы' },
  { value: 'closing', label: 'Закрывающие' },
  { value: 'other', label: 'Прочее' },
];

const ClientTicketQueue = ({ initialTicketId = null, initialTicketNonce = 0 }) => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [workspaceTab, setWorkspaceTab] = useState('ticket');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('waiting_crm');
  const [counts, setCounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isRequestPanelOpen, setIsRequestPanelOpen] = useState(false);
  const [isRequestDetailLoading, setIsRequestDetailLoading] = useState(false);
  const [requestDetail, setRequestDetail] = useState(null);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [requestSearch, setRequestSearch] = useState('');
  const [requestCandidates, setRequestCandidates] = useState([]);
  const [selectedCreateRequest, setSelectedCreateRequest] = useState(null);
  const [isRequestsLoading, setIsRequestsLoading] = useState(false);
  const [createMessage, setCreateMessage] = useState('');
  const [createFiles, setCreateFiles] = useState([]);
  const [message, setMessage] = useState('');
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [files, setFiles] = useState([]);
  const [documentCustomers, setDocumentCustomers] = useState([]);
  const [selectedDocumentCustomerId, setSelectedDocumentCustomerId] = useState('');
  const [selectedDocumentCustomer, setSelectedDocumentCustomer] = useState(null);
  const [documentCustomerSearch, setDocumentCustomerSearch] = useState('');
  const [isDocumentCustomersLoading, setIsDocumentCustomersLoading] = useState(false);
  const [documentSearch, setDocumentSearch] = useState('');
  const [documentRequests, setDocumentRequests] = useState([]);
  const [selectedDocumentRequestId, setSelectedDocumentRequestId] = useState(null);
  const [isDocumentRequestsLoading, setIsDocumentRequestsLoading] = useState(false);
  const [isDocumentsPanelOpen, setIsDocumentsPanelOpen] = useState(false);
  const [documentType, setDocumentType] = useState('client_request');
  const [documentFiles, setDocumentFiles] = useState([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);
  const fileInputRef = useRef(null);
  const createFileInputRef = useRef(null);
  const documentFileInputRef = useRef(null);
  const selectedIdRef = useRef(null);
  const selectedDocumentRequestIdRef = useRef(null);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    selectedDocumentRequestIdRef.current = selectedDocumentRequestId;
  }, [selectedDocumentRequestId]);

  const fetchCounts = useCallback(async () => {
    try {
      const response = await api.get('/repairs/client-tickets/counts/', {
        params: {
          search: search.trim() || undefined,
        },
      });
      setCounts(response.data || {});
    } catch (error) {
      console.error('Ошибка загрузки счетчиков тикетов:', error);
    }
  }, [search]);

  const fetchTickets = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    try {
      const response = await api.get('/repairs/client-tickets/', {
        params: {
          status: status || undefined,
          search: search.trim() || undefined,
          page_size: 100,
        },
      });
      const items = unwrapList(response.data);
      setTickets(items);
      const currentSelectedId = selectedIdRef.current;
      if (items.length === 0 || (currentSelectedId && !items.some(item => Number(item.id) === Number(currentSelectedId)))) {
        setSelectedId(null);
        setSelectedTicket(null);
      }
    } catch (error) {
      console.error('Ошибка загрузки тикетов:', error);
      if (!silent) toast.error('Не удалось загрузить тикеты');
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [search, status]);

  const fetchTicketDetail = useCallback(async (id, { silent = false } = {}) => {
    if (!id) return;
    if (!silent) setIsDetailLoading(true);
    try {
      const response = await api.get(`/repairs/client-tickets/${id}/`);
      if (Number(selectedIdRef.current) !== Number(id)) return;
      setSelectedTicket(response.data);
      setSelectedDocumentRequestId(response.data?.repair_request || null);
      window.dispatchEvent(new CustomEvent('crm-client-ticket-read', {
        detail: { ticket_id: id, repair_request_id: response.data?.repair_request },
      }));
    } catch (error) {
      console.error('Ошибка загрузки тикета:', error);
      if (!silent) toast.error('Не удалось открыть тикет');
    } finally {
      if (!silent) setIsDetailLoading(false);
    }
  }, []);

  const fetchRequestDetail = useCallback(async (repairRequestId, { openPanel = false, reset = false } = {}) => {
    if (!repairRequestId) return;
    if (openPanel) {
      setIsRequestPanelOpen(true);
    }
    if (reset) {
      setRequestDetail(null);
    }
    setIsRequestDetailLoading(true);
    try {
      const response = await api.get(`/repairs/client-requests/${repairRequestId}/`);
      setRequestDetail(response.data);
    } catch (error) {
      console.error('Ошибка загрузки заявки из тикета:', error);
      toast.error('Не удалось открыть детали заявки');
    } finally {
      setIsRequestDetailLoading(false);
    }
  }, []);

  const openDocumentsForRequest = useCallback((requestId) => {
    if (!requestId) return;
    setWorkspaceTab('documents');
    setSelectedId(null);
    setSelectedTicket(null);
    setSelectedDocumentRequestId(Number(requestId));
    setRequestDetail(null);
  }, []);

  const openDocumentsPanelForRequest = useCallback((requestId) => {
    if (!requestId) return;
    setIsDocumentsPanelOpen(true);
    fetchRequestDetail(requestId, { reset: true });
  }, [fetchRequestDetail]);

  const fetchRequestCandidates = useCallback(async () => {
    if (!isCreatePanelOpen) return;
    setIsRequestsLoading(true);
    try {
      const response = await api.get('/repairs/client-requests/', {
        params: {
          search: requestSearch.trim() || undefined,
          status_group: 'active',
          page_size: 50,
        },
      });
      const items = unwrapList(response.data);
      setRequestCandidates(items);
    } catch (error) {
      console.error('Ошибка загрузки заявок для тикета:', error);
      toast.error('Не удалось загрузить заявки');
    } finally {
      setIsRequestsLoading(false);
    }
  }, [isCreatePanelOpen, requestSearch]);

  const fetchDocumentCustomers = useCallback(async () => {
    if (workspaceTab !== 'documents') return;
    const normalizedSearch = documentCustomerSearch.trim();
    if (normalizedSearch.length < 2) {
      setDocumentCustomers([]);
      setIsDocumentCustomersLoading(false);
      return;
    }
    setIsDocumentCustomersLoading(true);
    try {
      const response = await api.get('/core/customers/', {
        params: {
          search: normalizedSearch,
          page_size: 20,
        },
      });
      const items = unwrapList(response.data);
      setDocumentCustomers(items);
      if (selectedDocumentCustomerId && !items.some(item => String(item.id) === String(selectedDocumentCustomerId))) {
        const selectedStillValid = selectedDocumentCustomer && String(selectedDocumentCustomer.id) === String(selectedDocumentCustomerId);
        if (!selectedStillValid && normalizedSearch) {
          setSelectedDocumentCustomerId('');
          setSelectedDocumentCustomer(null);
          setSelectedDocumentRequestId(null);
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки заказчиков для документов:', error);
      toast.error('Не удалось загрузить заказчиков');
    } finally {
      setIsDocumentCustomersLoading(false);
    }
  }, [documentCustomerSearch, selectedDocumentCustomer, selectedDocumentCustomerId, workspaceTab]);

  const fetchDocumentRequests = useCallback(async () => {
    if (workspaceTab !== 'documents') return;
    const normalizedSearch = documentSearch.trim();
    if (!selectedDocumentCustomerId && !normalizedSearch) {
      setDocumentRequests([]);
      if (!selectedDocumentRequestIdRef.current) {
        setSelectedDocumentRequestId(null);
      }
      return;
    }
    setIsDocumentRequestsLoading(true);
    try {
      const response = await api.get('/repairs/client-requests/', {
        params: {
          customer_id: normalizedSearch ? undefined : selectedDocumentCustomerId,
          search: normalizedSearch || undefined,
          page_size: 80,
        },
      });
      const items = unwrapList(response.data);
      setDocumentRequests(items);
      const currentSelectedRequestId = selectedDocumentRequestIdRef.current;
      if (currentSelectedRequestId && !items.some(item => Number(item.id) === Number(currentSelectedRequestId))) {
        setSelectedDocumentRequestId(null);
      }
    } catch (error) {
      console.error('Ошибка загрузки заявок для документов:', error);
      toast.error('Не удалось загрузить заявки');
    } finally {
      setIsDocumentRequestsLoading(false);
    }
  }, [documentSearch, selectedDocumentCustomerId, workspaceTab]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    if (selectedId) {
      fetchTicketDetail(selectedId);
    }
  }, [fetchTicketDetail, selectedId]);

  useEffect(() => {
    if (!selectedDocumentRequestId) {
      setRequestDetail(null);
      return;
    }
    fetchRequestDetail(selectedDocumentRequestId, { reset: true });
  }, [fetchRequestDetail, selectedDocumentRequestId]);

  useEffect(() => {
    fetchRequestCandidates();
  }, [fetchRequestCandidates]);

  useEffect(() => {
    const timer = window.setTimeout(fetchDocumentCustomers, 300);
    return () => window.clearTimeout(timer);
  }, [fetchDocumentCustomers]);

  useEffect(() => {
    fetchDocumentRequests();
  }, [fetchDocumentRequests]);

  useEffect(() => {
    if (!initialTicketId) return;
    setStatus('');
    setSelectedId(Number(initialTicketId));
  }, [initialTicketId, initialTicketNonce]);

  useEffect(() => {
    const pendingRequestId = localStorage.getItem('client_ticket_open_documents_request_id');
    if (!pendingRequestId) return;
    localStorage.removeItem('client_ticket_open_documents_request_id');
    openDocumentsForRequest(pendingRequestId);
  }, [openDocumentsForRequest]);

  useEffect(() => {
    const handleOpenTicket = (event) => {
      const ticketId = event?.detail?.ticket_id || event?.detail?.ticketId;
      if (!ticketId) return;
      setStatus('');
      setSelectedId(Number(ticketId));
    };
    window.addEventListener('crm-open-client-ticket', handleOpenTicket);
    return () => window.removeEventListener('crm-open-client-ticket', handleOpenTicket);
  }, []);

  useEffect(() => {
    const handleOpenDocuments = (event) => {
      const requestId = event?.detail?.repair_request_id || event?.detail?.repairRequestId;
      openDocumentsForRequest(requestId);
    };
    window.addEventListener('crm-open-client-documents', handleOpenDocuments);
    return () => window.removeEventListener('crm-open-client-documents', handleOpenDocuments);
  }, [openDocumentsForRequest]);

  const handleTicketRealtime = useCallback((payload) => {
    if (payload?.action === 'client_ticket_read') {
      fetchCounts();
      return;
    }
    fetchCounts();
    fetchTickets({ silent: true });
    const changedTicketId = payload?.ticket_id;
    if (selectedId && (!changedTicketId || Number(changedTicketId) === Number(selectedId))) {
      fetchTicketDetail(selectedId, { silent: true });
    }
  }, [fetchCounts, fetchTicketDetail, fetchTickets, selectedId]);

  useClientTicketsRealtime(handleTicketRealtime);

  const submitMessage = async () => {
    if (!selectedTicket) return;
    if (!message.trim() && files.length === 0) {
      toast.info('Введите ответ или приложите файл');
      return;
    }

    const formData = new FormData();
    formData.append('content', message);
    files.forEach(file => formData.append('attachments', file));

    try {
      const response = await api.post(`/repairs/client-tickets/${selectedTicket.id}/add_message/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSelectedTicket(response.data);
      setMessage('');
      setFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      fetchTickets({ silent: true });
      fetchCounts();
    } catch (error) {
      console.error('Ошибка отправки ответа:', error);
      toast.error('Не удалось отправить ответ');
    }
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    await submitMessage();
  };

  const handleReplyKeyDown = async (event) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    await submitMessage();
  };

  const resetCreateTicket = () => {
    setRequestSearch('');
    setRequestCandidates([]);
    setSelectedCreateRequest(null);
    setCreateMessage('');
    setCreateFiles([]);
    if (createFileInputRef.current) {
      createFileInputRef.current.value = '';
    }
  };

  const closeCreatePanel = () => {
    setIsCreatePanelOpen(false);
    resetCreateTicket();
  };

  const closeSelectedTicket = () => {
    setSelectedId(null);
    setSelectedTicket(null);
    setIsRequestPanelOpen(false);
    setIsDocumentsPanelOpen(false);
    setRequestDetail(null);
    setMessage('');
    setFiles([]);
    setDocumentFiles([]);
    setEditingMessageId(null);
    setEditingContent('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (documentFileInputRef.current) {
      documentFileInputRef.current.value = '';
    }
  };

  const handleCreateTicket = async (event) => {
    event.preventDefault();
    if (!selectedCreateRequest) {
      toast.info('Выберите заявку');
      return;
    }
    if (!createMessage.trim() && createFiles.length === 0) {
      toast.info('Введите сообщение или приложите файл');
      return;
    }

    const formData = new FormData();
    formData.append('repair_request', selectedCreateRequest.id);
    formData.append('content', createMessage);
    createFiles.forEach(file => formData.append('attachments', file));

    try {
      const response = await api.post('/repairs/client-tickets/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      closeCreatePanel();
      setStatus('');
      setWorkspaceTab('ticket');
      setSelectedId(response.data.id);
      setSelectedTicket(response.data);
      setSelectedDocumentRequestId(response.data?.repair_request || null);
      fetchTickets({ silent: true });
      fetchCounts();
      toast.success('Тикет создан');
    } catch (error) {
      console.error('Ошибка создания тикета:', error);
      toast.error(error.response?.data?.error || 'Не удалось создать тикет');
    }
  };

  const startEditMessage = (item) => {
    setEditingMessageId(item.id);
    setEditingContent(item.content || '');
  };

  const cancelEditMessage = () => {
    setEditingMessageId(null);
    setEditingContent('');
  };

  const saveEditedMessage = async () => {
    if (!selectedTicket || !editingMessageId) return;
    if (!editingContent.trim()) {
      toast.info('Введите текст сообщения');
      return;
    }
    try {
      const response = await api.patch(
        `/repairs/client-tickets/${selectedTicket.id}/messages/${editingMessageId}/`,
        { content: editingContent.trim() }
      );
      setSelectedTicket(response.data);
      cancelEditMessage();
      fetchTickets({ silent: true });
      fetchCounts();
    } catch (error) {
      console.error('Ошибка редактирования сообщения:', error);
      toast.error('Не удалось изменить сообщение');
    }
  };

  const deleteMessage = async (item) => {
    if (!selectedTicket || !item?.id) return;
    const confirmed = window.confirm('Удалить сообщение тикета?');
    if (!confirmed) return;
    try {
      const response = await api.delete(`/repairs/client-tickets/${selectedTicket.id}/messages/${item.id}/`);
      setSelectedTicket(response.data);
      fetchTickets({ silent: true });
      fetchCounts();
    } catch (error) {
      console.error('Ошибка удаления сообщения:', error);
      toast.error('Не удалось удалить сообщение');
    }
  };

  const openRepairRequest = async () => {
    if (!selectedTicket?.repair_request) return;
    fetchRequestDetail(selectedTicket.repair_request, { openPanel: true, reset: !requestDetail });
  };

  const uploadRequestDocuments = async () => {
    if (!requestDetail?.id) {
      toast.info('Выберите заявку');
      return;
    }
    if (documentFiles.length === 0) {
      toast.info('Выберите документы');
      return;
    }

    setIsUploadingDocuments(true);
    try {
      await Promise.all(documentFiles.map((file) => {
        const formData = new FormData();
        formData.append('repair_request', requestDetail.id);
        formData.append('document_type', documentType);
        formData.append('file', file);
        return api.post('/repairs/request-documents/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }));
      setDocumentFiles([]);
      if (documentFileInputRef.current) {
        documentFileInputRef.current.value = '';
      }
      fetchRequestDetail(requestDetail.id);
      toast.success('Документы прикреплены');
    } catch (error) {
      console.error('Ошибка загрузки документов заявки:', error);
      toast.error('Не удалось прикрепить документы');
    } finally {
      setIsUploadingDocuments(false);
    }
  };

  const deleteRequestDocument = async (document) => {
    const documentId = document?.meta?.document_id;
    if (!requestDetail?.id || !documentId) return;
    const confirmed = window.confirm('Удалить документ из заявки?');
    if (!confirmed) return;
    try {
      await api.delete(`/repairs/request-documents/${documentId}/`);
      fetchRequestDetail(requestDetail.id, { reset: false });
      toast.success('Документ удален');
    } catch (error) {
      console.error('Ошибка удаления документа заявки:', error);
      toast.error('Не удалось удалить документ');
    }
  };

  const replaceRequestDocument = async (document, file) => {
    const documentId = document?.meta?.document_id;
    if (!requestDetail?.id || !documentId || !file) return;
    const formData = new FormData();
    formData.append('document_type', document.type || documentType);
    formData.append('file', file);
    try {
      await api.patch(`/repairs/request-documents/${documentId}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      fetchRequestDetail(requestDetail.id, { reset: false });
      toast.success('Документ заменен');
    } catch (error) {
      console.error('Ошибка замены документа заявки:', error);
      toast.error('Не удалось заменить документ');
    }
  };

  const getStatusCount = (value) => {
    if (value === '') return counts.all || 0;
    return counts[value] || 0;
  };

  const getDocumentsBySection = (documents = []) => (
    documentSections.map(section => ({
      ...section,
      items: documents.filter(document => {
        const typeConfig = documentTypes.find(item => item.value === document.type);
        return (document.category || typeConfig?.category || 'other') === section.value;
      }),
    })).filter(section => section.value !== 'other' || section.items.length > 0)
  );

  const renderDocumentsWorkspace = () => {
    if (isRequestDetailLoading && !requestDetail) {
      return (
        <div className={styles.documentPanelState}>
          <div className={styles.spinner} />
          <span>Загрузка документов...</span>
        </div>
      );
    }

    if (!requestDetail) {
      return (
        <div className={styles.documentPanelState}>
          <FileText size={34} />
          <span>Документы появятся после загрузки заявки.</span>
        </div>
      );
    }

    return (
      <>
        <div className={styles.documentsHeader}>
          <div>
            <span>Документы заявки</span>
            <strong>{requestDetail.request_number}</strong>
          </div>
          {requestDetail.closing_documents_required && <em>Нужны закрывающие</em>}
        </div>

        <div className={styles.documentUploadBox}>
          <select
            value={documentType}
            onChange={(event) => setDocumentType(event.target.value)}
            className={styles.documentSelect}
          >
            {documentTypes.map(item => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
          <label className={styles.documentFileButton}>
            <Paperclip size={16} />
            <span>{documentFiles.length ? `Выбрано: ${documentFiles.length}` : 'Выбрать документы'}</span>
            <input
              ref={documentFileInputRef}
              type="file"
              multiple
              onChange={(event) => setDocumentFiles(Array.from(event.target.files || []))}
            />
          </label>
          <button
            type="button"
            className={styles.documentUploadButton}
            onClick={uploadRequestDocuments}
            disabled={isUploadingDocuments || documentFiles.length === 0}
          >
            <FileText size={16} />
            {isUploadingDocuments ? 'Загрузка...' : 'Прикрепить к заявке'}
          </button>
        </div>

        {requestDetail.documents?.length ? (
          <div className={styles.workspaceDocumentSections}>
            {getDocumentsBySection(requestDetail.documents).map(section => (
              <section key={section.value} className={styles.workspaceDocumentSection}>
                <div className={styles.workspaceDocumentSectionHead}>
                  <strong>{section.label}</strong>
                  <em>{section.items.length}</em>
                </div>
                {section.items.length ? (
                  <div className={styles.workspaceDocumentList}>
                    {section.items.map((document, index) => {
                      const canManageDocument = Boolean(document.meta?.manual && document.meta?.document_id);
                      return (
                        <div
                          key={`${document.type}-${document.url}-${index}`}
                          className={styles.workspaceDocumentItem}
                        >
                          <a
                            href={document.url}
                            target="_blank"
                            rel="noreferrer"
                            className={styles.workspaceDocumentLink}
                          >
                            <FileText size={18} />
                            <span>
                              <strong>{document.title}</strong>
                              <small>{document.filename}</small>
                              {document.source && <em>{document.source}</em>}
                            </span>
                          </a>
                          {canManageDocument && (
                            <div className={styles.documentActions}>
                              <label>
                                Заменить
                                <input
                                  type="file"
                                  onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    event.target.value = '';
                                    replaceRequestDocument(document, file);
                                  }}
                                />
                              </label>
                              <button type="button" onClick={() => deleteRequestDocument(document)}>
                                Удалить
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className={styles.requestEmptyLine}>Документов нет.</div>
                )}
              </section>
            ))}
          </div>
        ) : (
          <div className={styles.requestEmptyLine}>Документов пока нет.</div>
        )}
      </>
    );
  };

  const renderDocumentsCenter = () => (
    selectedDocumentRequestId ? (
      <div className={styles.documentsDetailCenter}>
        <button
          type="button"
          className={styles.documentBackButton}
          onClick={() => {
            setSelectedDocumentRequestId(null);
            setRequestDetail(null);
          }}
        >
          <X size={16} />
          К списку заявок
        </button>
        <section className={styles.documentWorkspacePane}>
          {renderDocumentsWorkspace()}
        </section>
      </div>
	    ) : (
	      <div className={styles.documentsCenter}>
	        <aside className={styles.documentCustomerPane}>
	          <div className={styles.documentsPaneTitle}>
	            <strong>Заказчик</strong>
	            <span>Найдите по названию или ИНН</span>
	          </div>
	          <div className={styles.documentCustomerSearchBox}>
	            <Search size={17} />
	            <input
	              value={documentCustomerSearch}
	              onChange={(event) => setDocumentCustomerSearch(event.target.value)}
	              placeholder="Введите минимум 2 символа"
	            />
	          </div>
	          <div className={styles.documentCustomerTools}>
	              <button
	                type="button"
	                className={styles.allCustomersButton}
	                onClick={() => {
	                  setSelectedDocumentCustomerId('');
	                  setSelectedDocumentCustomer(null);
	                  setSelectedDocumentRequestId(null);
	                  setDocumentCustomerSearch('');
	                }}
		              >
		                Искать заявку по всем заказчикам
		              </button>
	            {selectedDocumentCustomer && (
	              <div className={styles.selectedCustomerChip}>
	                <div className={styles.documentCustomerAvatar}>{getInitials(selectedDocumentCustomer.name)}</div>
	                <span>
	                  <strong>{selectedDocumentCustomer.name}</strong>
	                  {selectedDocumentCustomer.inn && <small>ИНН {selectedDocumentCustomer.inn}</small>}
	                </span>
	                <button
	                  type="button"
	                  onClick={() => {
	                    setSelectedDocumentCustomerId('');
	                    setSelectedDocumentCustomer(null);
	                    setSelectedDocumentRequestId(null);
	                  }}
	                >
	                  <X size={15} />
	                </button>
	              </div>
	            )}
	          </div>
	          {documentCustomerSearch.trim().length < 2 ? (
	            <div className={styles.compactEmpty}>Начните вводить заказчика или сразу найдите заявку ниже.</div>
	          ) : isDocumentCustomersLoading ? (
	            <div className={styles.compactState}>
	              <div className={styles.spinner} />
	              <span>Ищем заказчиков...</span>
	            </div>
	          ) : (
	            <div className={styles.documentCustomerList}>
	              {documentCustomers.map(customer => (
	                <button
	                  key={customer.id}
	                  type="button"
	                  className={`${styles.documentCustomerCard} ${String(selectedDocumentCustomerId) === String(customer.id) ? styles.documentCustomerCardActive : ''}`}
	                  onClick={() => {
	                    setSelectedDocumentCustomerId(String(customer.id));
	                    setSelectedDocumentCustomer(customer);
	                    setSelectedDocumentRequestId(null);
	                    setDocumentCustomerSearch(customer.name || '');
	                    setDocumentSearch('');
	                  }}
	                >
	                  <div className={styles.documentCustomerAvatar}>{getInitials(customer.name)}</div>
	                  <span>
	                    <strong>{customer.name}</strong>
	                    {customer.inn && <small>ИНН {customer.inn}</small>}
	                  </span>
	                </button>
	              ))}
	              {!documentCustomers.length && <div className={styles.compactEmpty}>Заказчики не найдены.</div>}
	            </div>
	          )}
        </aside>
        <aside className={styles.documentRequestPane}>
          <div className={styles.documentsPaneTitle}>
            <strong>Заявки</strong>
	            <span>
	              {documentSearch.trim()
	                ? 'Поиск по всем заказчикам'
	                : selectedDocumentCustomerId
	                  ? `По заказчику: ${selectedDocumentCustomer?.name || 'выбранный заказчик'}`
	                  : 'Введите номер заявки, серийник или выберите заказчика'}
	            </span>
          </div>
          <div className={styles.documentSearchBox}>
            <Search size={17} />
            <input
              value={documentSearch}
              onChange={(event) => setDocumentSearch(event.target.value)}
              placeholder="Заявка, заказчик или серийник"
            />
          </div>
          {selectedTicket?.repair_request && (
            <button
              type="button"
	              className={styles.currentTicketRequestButton}
	              onClick={() => {
	                setSelectedDocumentCustomerId(String(selectedTicket.customer || ''));
	                setSelectedDocumentCustomer({
	                  id: selectedTicket.customer,
	                  name: selectedTicket.customer_name,
	                });
	                setDocumentCustomerSearch(selectedTicket.customer_name || '');
	                setSelectedDocumentRequestId(selectedTicket.repair_request);
	              }}
            >
              <FileText size={16} />
              Документы выбранного тикета
            </button>
          )}
          {!selectedDocumentCustomerId && !documentSearch.trim() ? (
            <div className={styles.compactEmpty}>Выберите заказчика слева или введите номер заявки.</div>
          ) : isDocumentRequestsLoading ? (
            <div className={styles.compactState}>
              <div className={styles.spinner} />
              <span>Загрузка заявок...</span>
            </div>
          ) : (
            <div className={styles.documentRequestList}>
              {documentRequests.map(item => (
                <button
                  key={item.id}
                  type="button"
                  className={styles.documentRequestCard}
                  onClick={() => setSelectedDocumentRequestId(item.id)}
                >
                  <strong>{item.request_number}</strong>
                  <span>{item.customer_name}</span>
                  <small>{formatDate(item.created_at)} · {item.total_equipment} ед.</small>
                </button>
              ))}
              {!documentRequests.length && (
                <div className={styles.compactEmpty}>Заявки не найдены.</div>
              )}
            </div>
          )}
        </aside>
      </div>
    )
  );

  const renderRequestPanelContent = () => {
    if (isRequestDetailLoading) {
      return (
        <div className={styles.panelLoading}>
          <div className={styles.spinner} />
          <span>Загрузка заявки...</span>
        </div>
      );
    }

    if (!requestDetail) {
      return (
        <div className={styles.emptyState}>
          <MessageSquare size={42} />
          <h3>Заявка не загружена</h3>
          <p>Попробуйте открыть детали еще раз.</p>
        </div>
      );
    }

    return (
      <div className={styles.requestDetails}>
        <div className={styles.requestDetailsHeader}>
          <div>
            <span>{requestDetail.project_name}</span>
            <h2>{requestDetail.request_number}</h2>
            <p>{requestDetail.customer_name}</p>
          </div>
          <em>{requestDetail.status_display}</em>
        </div>

        <div className={styles.requestSummaryGrid}>
          <div>
            <span>Дата заявки</span>
            <strong>{formatDate(requestDetail.created_at)}</strong>
          </div>
          <div>
            <span>Сервисный центр</span>
            <strong>{requestDetail.service_center_name || '-'}</strong>
          </div>
          <div>
            <span>Оборудование</span>
            <strong>{requestDetail.total_equipment} ед.</strong>
          </div>
          <div>
            <span>Стоимость</span>
            <strong>{formatMoney(requestDetail.price_total)}</strong>
          </div>
        </div>

        <section className={styles.requestSection}>
          <div className={styles.requestSectionHeader}>
            <h3>Документы заявки</h3>
            {requestDetail.closing_documents_required && <em>Нужны закрывающие документы</em>}
          </div>
          {requestDetail.documents?.length ? (
            <div className={styles.documentList}>
              {requestDetail.documents.map((document, index) => (
                <a
                  key={`${document.type}-${document.url}-${index}`}
                  href={document.url}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.documentItem}
                >
                  <FileText size={18} />
                  <span>
                    <strong>{document.title}</strong>
                    <small>{document.filename}</small>
                  </span>
                </a>
              ))}
            </div>
          ) : (
            <div className={styles.requestEmptyLine}>Документов пока нет.</div>
          )}
        </section>

        <section className={styles.requestSection}>
          <h3>Оборудование</h3>
          <div className={styles.requestEquipmentList}>
            {(requestDetail.equipment_items || []).map(equipment => (
              <article key={equipment.id} className={styles.requestEquipmentCard}>
                <div className={styles.requestEquipmentTop}>
                  <div>
                    <strong>{equipment.serial_number}</strong>
                    <span>{equipment.equipment_model}</span>
                  </div>
                  <em>{equipment.public_status}</em>
                </div>
                <p>{equipment.declared_fault || 'Неисправность не указана'}</p>
                <div className={styles.requestFacts}>
                  <span>Ремонт: {equipment.repair_status_display}</span>
                  <span>Стоимость: {formatMoney(equipment.price_total)}</span>
                  {equipment.track_number && <span>Трек: {equipment.track_number}</span>}
                </div>
                {equipment.repair_items?.length > 0 && (
                  <div className={styles.requestFacts}>
                    {equipment.repair_items.map((item, index) => (
                      <span key={`${item.type}-${item.code || index}`}>
                        {item.name || item.code}: {formatMoney(item.price)}
                      </span>
                    ))}
                  </div>
                )}
                {equipment.history?.length > 0 && (
                  <details className={styles.historyBlock}>
                    <summary><History size={16} /> История серийника</summary>
                    {equipment.history.map(history => (
                      <div key={history.id} className={styles.historyRow}>
                        <span>{history.request_number}</span>
                        <span>{formatDate(history.created_at)}</span>
                        <span>{history.public_status}</span>
                        <span>{formatMoney(history.price_total)}</span>
                      </div>
                    ))}
                  </details>
                )}
              </article>
            ))}
          </div>
        </section>
      </div>
    );
  };

  return (
    <div className={styles.queue}>
      <div className={styles.workspaceTabs}>
        <button
          type="button"
          className={workspaceTab === 'ticket' ? styles.workspaceTabActive : styles.workspaceTab}
          onClick={() => setWorkspaceTab('ticket')}
        >
          <MessageSquare size={17} />
          Тикеты
        </button>
        <button
          type="button"
          className={workspaceTab === 'documents' ? styles.workspaceTabActive : styles.workspaceTab}
          onClick={() => setWorkspaceTab('documents')}
        >
          <FileText size={17} />
          Документы
        </button>
      </div>

      {workspaceTab === 'documents' ? (
        <section className={styles.documentsModePane}>
          {renderDocumentsCenter()}
        </section>
      ) : (
        <div className={styles.queueBody}>
          <section className={styles.listPane}>
        <div className={styles.controls}>
          <div className={styles.searchBox}>
            <Search size={18} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Поиск по заявке, заказчику или сообщению"
            />
          </div>
          <button type="button" className={styles.createTicketButton} onClick={() => setIsCreatePanelOpen(true)}>
            <Plus size={17} />
            Новый тикет
          </button>
          <div className={styles.statusTabs}>
            {statuses.map(item => (
              <button
                key={item.value || 'all'}
                type="button"
                className={status === item.value ? styles.statusActive : styles.statusButton}
                onClick={() => {
                  setStatus(item.value);
                  setSelectedId(null);
                }}
              >
                <span>{item.label}</span>
                <em>{getStatusCount(item.value)}</em>
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <span>Загрузка тикетов...</span>
          </div>
        ) : tickets.length === 0 ? (
          <div className={styles.emptyState}>
            <MessageSquare size={42} />
            <h3>Тикетов нет</h3>
            <p>В выбранном статусе нет обращений клиентов.</p>
          </div>
        ) : (
          <div className={styles.ticketList}>
            {tickets.map(ticket => (
              <button
                key={ticket.id}
                type="button"
	                className={`${styles.ticketCard} ${selectedId === ticket.id ? styles.ticketCardActive : ''} ${Number(ticket.unread_count || 0) > 0 ? styles.ticketCardUnread : ''}`}
                onClick={() => {
                  setWorkspaceTab('ticket');
                  setSelectedId(ticket.id);
                  setSelectedDocumentRequestId(ticket.repair_request || null);
                }}
              >
	                <div className={styles.ticketTop}>
	                  <div className={styles.ticketIdentity}>
	                    <div className={styles.ticketAvatar}>{getInitials(ticket.customer_name)}</div>
	                    <div>
	                      <strong>{ticket.repair_request_number}</strong>
	                      <span>{ticket.customer_name}</span>
	                    </div>
	                  </div>
	                  <em>{ticket.status_display}</em>
	                </div>
	                {Number(ticket.unread_count || 0) > 0 && (
	                  <div className={styles.unreadLine}>Требует ответа: {ticket.unread_count}</div>
	                )}
	                <p>{getTicketPreview(ticket)}</p>
	                <div className={styles.ticketMetaLine}>
	                  <span>{ticket.project_name}</span>
	                  <time>{formatDate(ticket.last_message_at || ticket.created_at)}</time>
	                </div>
	              </button>
            ))}
          </div>
        )}
          </section>

          <section className={styles.detailPane}>
        {isDetailLoading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <span>Открываем тикет...</span>
          </div>
        ) : !selectedTicket ? (
          <div className={styles.emptyState}>
            <MessageSquare size={42} />
            <h3>Выберите тикет</h3>
            <p>Переписка появится здесь. Документы доступны в соседней вкладке.</p>
          </div>
        ) : (
          <>
	            <div className={styles.detailHeader}>
	              <div className={styles.detailTitle}>
                <span>{selectedTicket.project_name}</span>
                <h2>{selectedTicket.repair_request_number}</h2>
                <p>{selectedTicket.customer_name}</p>
              </div>
              <div className={styles.detailActions}>
                <button type="button" className={styles.openButton} onClick={openRepairRequest}>
                  <ExternalLink size={16} />
                  Детали заявки
                </button>
                <button type="button" className={styles.closeChatButton} onClick={closeSelectedTicket}>
                  <X size={16} />
                  Закрыть чат
                </button>
              </div>
            </div>

            <div className={styles.ticketWorkspace}>
              <div className={styles.conversationColumn}>
	                <div className={styles.messages}>
	                  {(selectedTicket.messages || []).length ? (
	                    (selectedTicket.messages || []).map(item => (
	                      <div
	                        key={item.id}
	                        className={`${styles.messageRow} ${!item.is_from_guest ? styles.messageRowOwn : ''}`}
	                      >
	                        <div className={styles.messageAvatar}>{getInitials(item.author_name)}</div>
	                        <article className={`${styles.messageBubble} ${!item.is_from_guest ? styles.messageBubbleOwn : ''}`}>
	                          <div className={styles.messageHead}>
	                            <strong>{item.author_name}</strong>
	                            <div className={styles.messageMeta}>
	                              <span>{formatDate(item.created_at)}</span>
	                              <button type="button" onClick={() => startEditMessage(item)} title="Редактировать">
	                                <Edit3 size={14} />
	                              </button>
	                              <button type="button" onClick={() => deleteMessage(item)} title="Удалить">
	                                <Trash2 size={14} />
	                              </button>
	                            </div>
	                          </div>
	                          {editingMessageId === item.id ? (
	                            <div className={styles.editMessageForm}>
	                              <textarea
	                                value={editingContent}
	                                onChange={(event) => setEditingContent(event.target.value)}
	                                rows={3}
	                              />
	                              <div>
	                                <button type="button" onClick={saveEditedMessage}>
	                                  <Check size={15} />
	                                  Сохранить
	                                </button>
	                                <button type="button" onClick={cancelEditMessage}>
	                                  <X size={15} />
	                                  Отмена
	                                </button>
	                              </div>
	                            </div>
	                          ) : (
	                            item.content && <p>{item.content}</p>
	                          )}
	                          {item.attachments?.length > 0 && (
	                            <div className={styles.attachments}>
	                              {item.attachments.map(attachment => (
	                                <a key={attachment.id} href={attachment.url} target="_blank" rel="noreferrer">
	                                  <Paperclip size={14} />
	                                  {attachment.original_name || 'Файл'}
	                                </a>
	                              ))}
	                            </div>
	                          )}
	                        </article>
	                      </div>
	                    ))
	                  ) : (
	                    <div className={styles.chatEmptyState}>Сообщений пока нет.</div>
	                  )}
	                </div>

                <form className={styles.replyForm} onSubmit={handleSendMessage}>
                  <textarea
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    onKeyDown={handleReplyKeyDown}
                    placeholder="Ответ клиенту"
                    rows={4}
                  />
                  <div className={styles.formActions}>
                    <label className={styles.fileButton}>
                      <Paperclip size={16} />
                      <span>{files.length ? `Файлов: ${files.length}` : 'Прикрепить файл'}</span>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={(event) => setFiles(Array.from(event.target.files || []))}
                      />
                    </label>
                    <button type="submit" className={styles.sendButton}>
                      <Send size={16} />
                      Ответить
                    </button>
                  </div>
	                </form>
	              </div>
	              <aside className={styles.ticketContextPanel}>
	                <div className={styles.contextCard}>
	                  <span>Заявка</span>
	                  <strong>{selectedTicket.repair_request_number}</strong>
	                  <p>{selectedTicket.customer_name}</p>
	                </div>
	                <div className={styles.contextFacts}>
	                  <div>
	                    <span>Раздел</span>
	                    <strong>{selectedTicket.project_name || '-'}</strong>
	                  </div>
	                  <div>
	                    <span>Статус тикета</span>
	                    <strong>{selectedTicket.status_display || '-'}</strong>
	                  </div>
	                  <div>
	                    <span>Последнее сообщение</span>
	                    <strong>{formatDate(selectedTicket.last_message_at || selectedTicket.created_at)}</strong>
	                  </div>
	                </div>
	                <button type="button" className={styles.contextButton} onClick={() => openDocumentsPanelForRequest(selectedTicket.repair_request)}>
	                  <FileText size={16} />
	                  Документы заявки
	                </button>
	              </aside>
	            </div>
          </>
        )}
          </section>
        </div>
      )}

      <SlidingPanel
        isOpen={isRequestPanelOpen}
        onClose={() => setIsRequestPanelOpen(false)}
        title="Детали заявки"
        width="880px"
      >
        {renderRequestPanelContent()}
      </SlidingPanel>

      <SlidingPanel
        isOpen={isDocumentsPanelOpen}
        onClose={() => setIsDocumentsPanelOpen(false)}
        title="Документы заявки"
        width="760px"
      >
        <div className={styles.documentPanelDrawer}>
          {renderDocumentsWorkspace()}
        </div>
      </SlidingPanel>

      <SlidingPanel
        isOpen={isCreatePanelOpen}
        onClose={closeCreatePanel}
        title="Новый тикет"
        width="760px"
      >
        <div className={styles.createTicketPanel}>
          <div className={styles.createSearchBox}>
            <Search size={18} />
            <input
              value={requestSearch}
              onChange={(event) => setRequestSearch(event.target.value)}
              placeholder="Номер заявки, заказчик или серийный номер"
            />
          </div>

          <div className={styles.createGrid}>
            <div className={styles.requestPicker}>
              {isRequestsLoading ? (
                <div className={styles.panelLoading}>
                  <div className={styles.spinner} />
                  <span>Загрузка заявок...</span>
                </div>
              ) : requestCandidates.length ? (
                requestCandidates.map(item => (
                  <button
                    key={item.id}
                    type="button"
                    className={`${styles.requestPickCard} ${selectedCreateRequest?.id === item.id ? styles.requestPickCardActive : ''}`}
                    onClick={() => setSelectedCreateRequest(item)}
                  >
                    <strong>{item.request_number}</strong>
                    <span>{item.customer_name}</span>
                    <em>{item.project_name}</em>
                    <small>{formatDate(item.created_at)} · {item.total_equipment} ед.</small>
                  </button>
                ))
              ) : (
                <div className={styles.emptyState}>
                  <MessageSquare size={42} />
                  <h3>Заявки не найдены</h3>
                  <p>Уточните поиск или проверьте активные заявки.</p>
                </div>
              )}
            </div>

            <form className={styles.createForm} onSubmit={handleCreateTicket}>
              <div className={styles.createSelected}>
                <span>Выбранная заявка</span>
                <strong>{selectedCreateRequest?.request_number || 'Не выбрана'}</strong>
                {selectedCreateRequest && <em>{selectedCreateRequest.customer_name}</em>}
              </div>
              <textarea
                value={createMessage}
                onChange={(event) => setCreateMessage(event.target.value)}
                placeholder="Напишите вопрос клиенту по заявке"
                rows={7}
              />
              <div className={styles.formActions}>
                <label className={styles.fileButton}>
                  <Paperclip size={16} />
                  <span>{createFiles.length ? `Файлов: ${createFiles.length}` : 'Прикрепить файл'}</span>
                  <input
                    ref={createFileInputRef}
                    type="file"
                    multiple
                    onChange={(event) => setCreateFiles(Array.from(event.target.files || []))}
                  />
                </label>
                <button type="submit" className={styles.sendButton}>
                  <Send size={16} />
                  Создать
                </button>
              </div>
            </form>
          </div>
        </div>
      </SlidingPanel>
    </div>
  );
};

export default ClientTicketQueue;
